import { LocationWindow } from '../LocationWindow'
import { useState } from 'react'
import type { Clan, ClanMember } from '@mezha/api'
import { useClanCommandMutation, useClanQuery, useClansQuery } from '../../api/endpoints/social'
import { useHeroQuery } from '../../api/endpoints/hero'
import { useMeQuery } from '../../api/endpoints/auth'
import { useCommand } from '../../lib/useCommand'
import { usePhone } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { ActionButton, ConfirmInPlace, PlankButton, cx } from '../../ui'
import './clans.css'

type Confirm =
  | { kind: 'leave' }
  | { kind: 'kick' | 'transfer'; member: ClanMember }

const ROLE_RU: Record<string, string> = { owner: 'глава', member: 'участник' }

/** `/play/clans`: list, card, founding for silver, joining and leaving, the owner's tools. */
export function Clans() {
  useShellMode('window')
  const list = useClansQuery()
  const phone = usePhone()
  const [picked, setPicked] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [listOnPhone, setListOnPhone] = useState(false)
  const mine = list.data?.mine ?? null
  const shownId = picked ?? mine?.id ?? null

  const listView = (
    <div className="clans__list">
      <div className="eyebrow">Кланы Межи</div>
      {list.data && list.data.items.length === 0 && <p className="note">Кланов ещё нет — можно основать первый.</p>}
      <ul className="plist">
        {list.data?.items.map(c => (
          <li key={c.id}>
            <button type="button" className={cx('prow', shownId === c.id && 'is-selected')} onClick={() => { setCreating(false); setPicked(c.id); setListOnPhone(false) }}>
              <Emblem name="nav.clans" /><span className="name">{c.name}<small>глава {c.owner_name}</small></span>
              {mine?.id === c.id && <span className="tag">ваш</span>}
              <span className="mod">{c.members}</span>
            </button>
          </li>
        ))}
      </ul>
      {!mine && <PlankButton gold onClick={() => { setPicked(null); setCreating(true); setListOnPhone(false) }}>Основать клан</PlankButton>}
    </div>
  )

  const detail = creating ? <CreateClan price={list.data?.create_price ?? 0} onDone={() => setCreating(false)} />
    : shownId !== null ? <ClanCard id={shownId} mine={mine} onBack={phone ? () => { setPicked(null); setListOnPhone(true) } : undefined} />
    : <Empty>Выберите клан, чтобы увидеть участников.</Empty>

  return (
    <LocationWindow title="Кланы" emblem="nav.clans" idBase="clans" bodyClassName="clans">
      {list.isLoading && <Loading />}
      {list.isError && !list.data && <Failure error={list.error} retry={() => void list.refetch()} what="Кланы" />}
      {list.data && (phone
        ? (creating || (shownId !== null && !listOnPhone) ? detail : listView)
        : <>{listView}{detail}</>)}
    </LocationWindow>
  )
}

function ClanCard({ id, mine, onBack }: { id: number; mine: Clan | null; onBack?: () => void }) {
  const clan = useClanQuery(id)
  const hero = useHeroQuery()
  const me = useMeQuery()
  const [command] = useClanCommandMutation()
  const cmd = useCommand(() => void clan.refetch())
  const [editing, setEditing] = useState(false)
  const dispatch = useAppDispatch()
  const [text, setText] = useState('')
  const c = clan.data
  const myId = me.data?.account_id
  const isMember = Boolean(c && mine?.id === c.id)
  const isOwner = Boolean(c && myId && c.owner_id === myId)
  const run = (path: string, body?: Record<string, unknown>) => cmd.run(() => command({ path, body }).unwrap())

  const inCombat = Boolean(hero.data?.in_combat)

  if (clan.isLoading) return <div className="clans__card"><Loading /></div>
  if (!c) return <div className="clans__card"><Failure error={clan.error} retry={() => void clan.refetch()} what="Клан" /></div>

  const act = async (confirm: Confirm) => {
    let ok: unknown
    if (confirm.kind === 'leave') ok = await run('/leave')
    if (confirm.kind === 'kick') ok = await run(`/${c.id}/kick`, { account_id: confirm.member.account_id })
    if (confirm.kind === 'transfer') ok = await run(`/${c.id}/transfer`, { account_id: confirm.member.account_id })
    if (ok) dispatch(ui.actions.notice(confirmDone(confirm, c)))
  }
  const join = async () => {
    if (await run(`/${c.id}/join`)) dispatch(ui.actions.notice(`Вы в клане «${c.name}»`))
  }

  return (
    <div className="clans__card">
      {onBack && <button type="button" className="linkbtn clans__back" onClick={onBack}>◂ ко всем кланам</button>}
      <div className="parch clans__head">
        <h2>{c.name}</h2>
        <p className="clans__meta">глава {c.owner_name} · {c.members} из {c.max_members} · основан {new Date(c.created_at).toLocaleDateString('ru-RU')}</p>
        {!editing && <p>{c.description || <i>Описания нет.</i>}</p>}
        {editing && (
          <div className="field">
            <label htmlFor="clan-desc">Описание</label>
            <textarea id="clan-desc" className="slab" value={text} maxLength={500} rows={3} onChange={e => setText(e.target.value)} />
            <small>{500 - text.length} знаков осталось</small>
            <div className="actions">
              <PlankButton gold onClick={async () => { if (await run(`/${c.id}/description`, { description: text })) setEditing(false) }}>Сохранить</PlankButton>
              <PlankButton onClick={() => setEditing(false)}>Отмена</PlankButton>
            </div>
          </div>
        )}
        {isOwner && !editing && <button type="button" className="linkbtn linkbtn--ink" onClick={() => { setText(c.description); setEditing(true) }}>Изменить описание</button>}
        {!mine && <div className="actions"><ActionButton gold locked={inCombat} onPress={join}>Вступить в «{c.name}»</ActionButton></div>}
        {isMember && !isOwner && (
          <ConfirmInPlace question={confirmText({ kind: 'leave' }, c)} yes="Да, выйти" onConfirm={() => act({ kind: 'leave' })}>
            {ask => <div className="actions"><PlankButton locked={inCombat} onClick={ask}>Выйти из клана</PlankButton></div>}
          </ConfirmInPlace>
        )}
      </div>
      <div className="eyebrow">Участники</div>
      <ul className="lboard">
        {c.list.map(m => (
          <li key={m.account_id} className={cx('brow', m.account_id === myId && 'is-me')}>
            <i>{m.level}</i>{m.name} <small className="clans__role">{ROLE_RU[m.role] ?? m.role}</small>
            {isOwner && m.account_id !== myId && (
              <span className="clans__ops">
                {(['transfer', 'kick'] as const).map(kind => (
                  <ConfirmInPlace key={kind} className="clans__ask" question={confirmText({ kind, member: m }, c)} yes={kind === 'kick' ? 'Исключить' : 'Передать'}
                    onConfirm={() => act({ kind, member: m })}>
                    {ask => <button type="button" className="linkbtn" onClick={ask}>{kind === 'kick' ? 'исключить' : 'передать'}</button>}
                  </ConfirmInPlace>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
      {isMember && <p className="note">Клановый чат — во вкладке «Клан» чата.{isOwner ? ' Главе, чтобы выйти, нужно сначала передать главенство.' : ''}</p>}
      {cmd.text && <p className="clans__error" role="alert">{cmd.text}</p>}
    </div>
  )
}

/** The question asked in place of the button. */
function confirmText(c: Confirm, clan: Clan): string {
  switch (c.kind) {
    case 'leave': return `Выйти из «${clan.name}»?`
    case 'kick': return `Исключить ${c.member.name} из клана?`
    case 'transfer': return `Передать главенство ${c.member.name}? Вы останетесь участником.`
  }
}

function confirmDone(c: Confirm, clan: Clan): string {
  switch (c.kind) {
    case 'leave': return `Вы вышли из «${clan.name}»`
    case 'kick': return `${c.member.name} исключён из клана`
    case 'transfer': return `Глава клана — ${c.member.name}`
  }
}

function CreateClan({ price, onDone }: { price: number; onDone: () => void }) {
  const hero = useHeroQuery()
  const [command] = useClanCommandMutation()
  const cmd = useCommand()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const silver = hero.data?.silver ?? 0
  const ready = name.trim().length >= 3
  const create = async () => {
    if (await cmd.run(() => command({ path: '', body: { name: name.trim(), description } }).unwrap())) onDone()
  }
  return (
    <div className="clans__card">
      <div className="parch clans__head">
        <h2>Новый клан</h2>
        <p className="clans__meta">Цена основания — {price} серебра, у вас {silver}.</p>
        <div className="field">
          <label htmlFor="clan-name">Название</label>
          <input id="clan-name" className="slab" value={name} maxLength={32} onChange={e => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="clan-about">Описание</label>
          <textarea id="clan-about" className="slab" rows={3} maxLength={500} value={description} onChange={e => setDescription(e.target.value)} />
          <small>{500 - description.length} знаков осталось</small>
        </div>
        <ConfirmInPlace question={<>Основать «{name.trim()}» за <b>{price}</b> серебра? У вас {silver}.</>} yes="Да, основать" onConfirm={create}>
          {ask => (
            <div className="actions">
              <PlankButton gold disabled={!ready} onClick={ask}>Основать за {price} серебра</PlankButton>
              <PlankButton onClick={onDone}>Отмена</PlankButton>
            </div>
          )}
        </ConfirmInPlace>
        {cmd.text && <p className="clans__error" role="alert">{cmd.text}</p>}
      </div>
    </div>
  )
}
